/**
 * Dancer Constants
 * Mirrors the exact constants from Flow PWA (Meteor/MongoDB)
 *
 * Sources:
 * - Legacy PWA migration (Meteor/MongoDB constants)
 * - Database enums: packages/supabase/database.types.ts
 *
 * These constants match the database enum values exactly for bijective mapping
 * during data migration from PWA to Supabase.
 */
import {
  GENDER_LABELS,
  GENDER_OPTIONS,
  GENDER_VALUES,
} from '@kit/shared/gender';
import type { Database } from '@kit/supabase/database';

// ============================================================
// Type Aliases for Database Enums
// ============================================================

export type GenderType = Database['public']['Enums']['gender_type'];
export type LookingForType = Database['public']['Enums']['looking_for_type'];
export type DanceStyleType = Database['public']['Enums']['dance_style_type'];
export type DanceLevelType = Database['public']['Enums']['dance_level_type'];

// ============================================================
// Gender Constants (legacy: individuals/candidates)
// ============================================================

export const GENDER = {
  MALE: 'male',
  FEMALE: 'female',
  NON_BINARY: 'nonBinary', // camelCase matches PWA exactly
} as const;

// Values, labels and option lists come from the canonical gender registry
// (@kit/shared/gender) — single source of truth, no parallel maps (Rule #8).
// Imported at the top so local helpers (getGenderLabel) can use them; re-exported
// here to preserve the existing `@kit/artists` consumer API.
export { GENDER_VALUES, GENDER_LABELS, GENDER_OPTIONS };

// ============================================================
// Looking For Constants (legacy: individuals/candidates)
// ============================================================

export const LOOKING_FOR = {
  SCHOOL: 'school',
  COMPANY: 'company',
  SUMMER_INTENSIVE: 'summerIntensive', // camelCase matches PWA
  COMPETITION: 'competition',
  JUST_LOOKING: 'justLooking', // camelCase matches PWA
} as const;

export const LOOKING_FOR_VALUES: LookingForType[] = Object.values(
  LOOKING_FOR,
) as LookingForType[];

export const LOOKING_FOR_LABELS: Record<LookingForType, string> = {
  // Legacy vocabulary (kept for backward compatibility — existing dancer
  // rows still reference these).
  school: 'School',
  company: 'Company',
  summerIntensive: 'Summer Intensive',
  competition: 'Competition',
  justLooking: 'Just Looking',
  // Phase 2 dancer-profile redesign (Philippine's brief 2026-05-12):
  // "Available for" with 6 professional-engagement types.
  contract: 'Contract',
  campaigns: 'Campaigns',
  touring: 'Touring',
  guesting: 'Guesting',
  choreography: 'Choreography',
  teaching: 'Teaching',
  ballet_master: 'Ballet Master',
};

/**
 * All selectable `looking_for_type` values, in the order they appear in the
 * "Available for" selector (BAL-248 §11b). Derived from LOOKING_FOR_LABELS so
 * a future enum addition surfaces here automatically. `LOOKING_FOR_VALUES`
 * above is kept as the legacy 5-value list for backward-compat callers; the
 * editor selector uses this full list.
 */
export const LOOKING_FOR_ALL_VALUES: LookingForType[] = Object.keys(
  LOOKING_FOR_LABELS,
) as LookingForType[];

// ============================================================
// Dance Style Constants (from dance.js:3-8)
// ============================================================

export const DANCE_STYLE = {
  ACRODANCE: 'acrodance',
  AFRICAN: 'african',
  BACHATA: 'bachata',
  BALLROOM: 'ballroom',
  CHARACTER: 'character',
  CLASSICAL: 'classical',
  CONTEMPORARY: 'contemporary',
  FLAMENCO: 'flamenco',
  FOLK: 'folk',
  HEELS: 'heels',
  HIPHOP: 'hiphop',
  JAZZ: 'jazz',
  LATIN: 'latin',
  LYRICAL: 'lyrical',
  MODERN: 'modern',
  MUSICAL_THEATRE: 'musical_theatre',
  NEOCLASSICAL: 'neoclassical',
  POINTE: 'pointe',
  SALSA: 'salsa',
  SAMBA: 'samba',
  SHOWDANCE: 'showdance',
  TANGO: 'tango',
  TAP: 'tap',
} as const;

/**
 * Non-empty tuple (not `DanceStyleType[]`) so `z.enum(DANCE_STYLE_VALUES)`
 * type-checks: Zod requires `[string, ...string[]]`. Callers that need a
 * plain array can still spread it. See `../lib/dance-enums.ts` for the
 * normalizer that turns natural-language input into these values.
 */
export const DANCE_STYLE_VALUES: readonly [
  DanceStyleType,
  ...DanceStyleType[],
] = Object.values(DANCE_STYLE) as [DanceStyleType, ...DanceStyleType[]];

/**
 * Compile-time drift guard: if `pnpm supabase:web:typegen` adds a member to
 * the `dance_style_type` Postgres enum that `DANCE_STYLE` above does not
 * carry, `Exclude<...>` stops being `never` and this assignment fails
 * typecheck. Costs nothing at runtime.
 */
// Compare against DANCE_STYLE's literal value union, NOT
// `DANCE_STYLE_VALUES[number]`. The tuple carries an explicit
// `readonly [DanceStyleType, ...]` annotation, so its element type is the FULL
// enum union and `Exclude<DanceStyleType, DanceStyleType>` would be `never`
// unconditionally — a guard that can never fire. `DANCE_STYLE` is `as const`,
// so its value union stays literal and the check has teeth.
type AllDanceStylesCovered =
  Exclude<
    DanceStyleType,
    (typeof DANCE_STYLE)[keyof typeof DANCE_STYLE]
  > extends never
    ? true
    : never;
const _assertAllDanceStylesCovered: AllDanceStylesCovered = true;
void _assertAllDanceStylesCovered;

export const DANCE_STYLE_LABELS: Record<DanceStyleType, string> = {
  acrodance: 'Acrodance',
  african: 'African Dance',
  bachata: 'Bachata',
  ballroom: 'Ballroom',
  character: 'Character',
  classical: 'Classical',
  contemporary: 'Contemporary',
  flamenco: 'Flamenco',
  folk: 'Folk Dance',
  heels: 'Heels',
  hiphop: 'Hip-Hop',
  jazz: 'Jazz',
  latin: 'Latin Dance',
  lyrical: 'Lyrical',
  modern: 'Modern',
  musical_theatre: 'Musical Theatre',
  neoclassical: 'Neoclassical',
  pointe: 'Pointe',
  salsa: 'Salsa',
  samba: 'Samba',
  showdance: 'Showdance',
  tango: 'Tango',
  tap: 'Tap Dance',
};

// ============================================================
// Dance Level Constants (legacy: individuals/candidates)
// ============================================================

export const DANCE_LEVEL = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  PRO: 'pro',
  PRE_PRO: 'prePro', // camelCase matches PWA
} as const;

/** Non-empty tuple for `z.enum` — see `DANCE_STYLE_VALUES` above. */
export const DANCE_LEVEL_VALUES: readonly [
  DanceLevelType,
  ...DanceLevelType[],
] = Object.values(DANCE_LEVEL) as [DanceLevelType, ...DanceLevelType[]];

// Literal union, not the widened tuple element type — see the style guard above.
type AllDanceLevelsCovered =
  Exclude<
    DanceLevelType,
    (typeof DANCE_LEVEL)[keyof typeof DANCE_LEVEL]
  > extends never
    ? true
    : never;
const _assertAllDanceLevelsCovered: AllDanceLevelsCovered = true;
void _assertAllDanceLevelsCovered;

export const DANCE_LEVEL_LABELS: Record<DanceLevelType, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  pro: 'Professional',
  prePro: 'Pre-Professional',
};

// ============================================================
// Helper Functions
// ============================================================

/**
 * Get display label for gender
 */
export function getGenderLabel(gender: GenderType | null): string {
  if (!gender) return 'Not specified';
  return GENDER_LABELS[gender];
}

/**
 * Get display label for looking for option
 */
export function getLookingForLabel(lookingFor: LookingForType): string {
  return LOOKING_FOR_LABELS[lookingFor];
}

/**
 * Get display labels for array of looking for options
 */
export function getLookingForLabels(
  lookingForArray: LookingForType[] | null,
): string[] {
  if (!lookingForArray) return [];
  return lookingForArray.map((item) => getLookingForLabel(item));
}

/**
 * Get display label for dance style
 */
export function getDanceStyleLabel(style: DanceStyleType): string {
  return DANCE_STYLE_LABELS[style];
}

/**
 * Get display labels for array of dance styles
 */
export function getDanceStyleLabels(styles: DanceStyleType[] | null): string[] {
  if (!styles) return [];
  return styles.map((style) => getDanceStyleLabel(style));
}

/**
 * Get display label for dance level
 */
export function getDanceLevelLabel(level: DanceLevelType | null): string {
  if (!level) return 'Not specified';
  return DANCE_LEVEL_LABELS[level];
}
